/**
 * "Birine öner" — sends one item to another profile in the household.
 *
 * The profile list is fetched only when the panel is opened. It is a request per use of a
 * button most people press rarely, and loading it on every detail screen would mean the
 * whole household's profiles travel with every film anybody looks at.
 *
 * Re-recommending is not guarded against here, and that is deliberate: the server turns a
 * repeat into an update that returns the row to the recipient's inbox as unread. Someone
 * pressing this twice usually means it the second time.
 */
import { useState } from 'react';
import type { ProfileDto } from '@filmodasi/shared';
import { PROFILE_RECOMMENDATION_MESSAGE_MAX } from '@filmodasi/shared';
import { api } from '../api/client';
import { useSession } from '../store/session';
import { Avatar } from './Avatar';

export function RecommendButton({ itemId }: { itemId: string }) {
  const { profile: current } = useSession();
  const [open, setOpen] = useState(false);
  const [profiles, setProfiles] = useState<ProfileDto[] | null>(null);
  const [message, setMessage] = useState('');
  const [sentTo, setSentTo] = useState<string[]>([]);
  const [failure, setFailure] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function openPanel() {
    setOpen(true);
    setFailure(null);
    if (profiles) return;
    try {
      const all = await api<ProfileDto[]>('/api/profiles');
      // You cannot recommend to yourself — the server refuses it, so the option is not
      // offered rather than shown and then rejected.
      setProfiles(all.filter((p) => p.id !== current?.id));
    } catch (err) {
      setFailure(err instanceof Error ? err.message : 'Profiller yüklenemedi');
    }
  }

  async function send(receiverProfileId: string) {
    setBusy(true);
    setFailure(null);
    try {
      await api('/api/profile-recommendations', {
        method: 'POST',
        body: { itemId, receiverProfileId, message: message.trim() || null },
      });
      setSentTo((prev) => [...prev, receiverProfileId]);
    } catch (err) {
      setFailure(err instanceof Error ? err.message : 'Gönderilemedi');
    } finally {
      setBusy(false);
    }
  }

  if (!open) {
    return (
      <button type="button" onClick={() => void openPanel()}>
        Birine öner
      </button>
    );
  }

  return (
    <div className="recommend">
      <div className="recommend__head">
        <strong>Kime önereceksiniz?</strong>
        <button type="button" className="link" onClick={() => setOpen(false)}>
          Kapat
        </button>
      </div>

      {failure && <p className="error">{failure}</p>}

      <label className="recommend__note">
        Not (isteğe bağlı)
        <input
          type="text"
          value={message}
          maxLength={PROFILE_RECOMMENDATION_MESSAGE_MAX}
          placeholder="buna bayılacaksın"
          onChange={(e) => setMessage(e.target.value)}
        />
      </label>

      {profiles === null && <p className="recommend__hint">Yükleniyor…</p>}
      {profiles?.length === 0 && (
        <p className="recommend__hint">Önerebileceğiniz başka bir profil yok.</p>
      )}

      <div className="recommend__people">
        {profiles?.map((p) => {
          const done = sentTo.includes(p.id);
          return (
            <button
              key={p.id}
              type="button"
              className="recommend__person"
              disabled={busy}
              onClick={() => void send(p.id)}
            >
              <Avatar profile={p} size={40} />
              <span>{p.name}</span>
              {/* Stays clickable after sending: pressing it again is a deliberate second
                  nudge, which the server handles by reviving the row as unread. */}
              {done && <span className="recommend__done">gönderildi</span>}
            </button>
          );
        })}
      </div>
    </div>
  );
}
